import { sendMessageToExtension } from "../utils/postToExtension";

const getAllLocalStorageValues = () => {
  const values = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    const value = localStorage.getItem(key);
    values[key] = value;
  }
  return values;
};

const sendUpdatedValuesToExtension = () => {
  const allValues = getAllLocalStorageValues();

  sendMessageToExtension({
    message: "save-value-to-storage",
    data: { ...allValues },
  });
};

// The cached "user" object drives the identity shown in the navbar. It gets rewritten
// out of band (login, /me refresh, end of onboarding, profile save), and the browser's
// own `storage` event only fires in *other* tabs — so announce our own writes here and
// let AccountProvider re-sync instead of staying stale until a full reload.
const USER_CACHE_EVENT = "user-cache-updated";

const notifyUserCacheUpdated = (key) => {
  if (key !== "user") {
    return;
  }

  try {
    window.dispatchEvent(new Event(USER_CACHE_EVENT));
  } catch {
    //
  }
};

const saveDataToLocalStorage = (key, value, isNotForExtension = false) => {
  localStorage.setItem(key, value);
  if (isNotForExtension) {
    return;
  }

  sendUpdatedValuesToExtension();
};

const saveObjectToLocalStorage = (key, object, isNotForExtension = false) => {
  const value = JSON.stringify(object);
  localStorage.setItem(key, value);
  notifyUserCacheUpdated(key);
  if (isNotForExtension) {
    return;
  }

  sendUpdatedValuesToExtension();
};

const getDataFromLocalStorage = (key) => {
  return localStorage.getItem(key);
};

const getObjectFromLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    const value = JSON.parse(item);
    return value;
  } catch (error) {
    console.error("getObjectFromLocalStorage error:", error);
    return undefined;
  }
};

const removeDataFromLocalStorage = (key, isNotForExtension = false) => {
  localStorage.removeItem(key);
  if (isNotForExtension) {
    return;
  }

  sendUpdatedValuesToExtension();
};

const removeDataListFromLocalStorage = (keys) => {
  for (const key of keys) {
    localStorage.removeItem(key);
  }

  sendUpdatedValuesToExtension();
};

const CREATE_CAMPAIGN_DATA_KEYS = [
  "jobTitle",
  "jobDescription",
  "payRate",
  "campaignInfo",
];

const CLEAR_CACHE_EXCLUDE_KEYS = [
  "extensionId",
  "extensionVersion",
  "refreshToken",
  "MinExtensionVersion",
  "accessToken",
  "user",
  "version",
  "src",
];

const removeCreateCampaignDataFromLocalStorage = () => {
  removeDataListFromLocalStorage(CREATE_CAMPAIGN_DATA_KEYS);
};

const removeClearCacheDataFromLocalStorage = () => {
  // Snapshot the keys first: removing while iterating localStorage.length shifts
  // the indices and skips roughly every other key.
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    keys.push(localStorage.key(i));
  }
  for (const key of keys) {
    if (!CLEAR_CACHE_EXCLUDE_KEYS.includes(key)) {
      localStorage.removeItem(key);
    }
  }
};

const removeLoginDataFromLocalStorage = () => {
  const keys = [
    "token",
    "user",
    "accessToken",
    "refreshToken",
    "isPostSignUpOnboardingFinishLater",
    "companyInfo",
    // Still written by the /min-campaigns-prep flow the deployed SalesStream
    // extension injects; a stale value across logins triggers a phantom prep bar.
    "campaignToMinimize",
    "campaignToSelect",
    "isImpersonation",
    "impersonatedEmail",
    "awaitingPrep",
    ...CREATE_CAMPAIGN_DATA_KEYS,
  ];
  removeDataListFromLocalStorage(keys);
};

const clearLocalStorage = () => {
  localStorage.clear();
};

export {
  USER_CACHE_EVENT,
  saveDataToLocalStorage,
  saveObjectToLocalStorage,
  getDataFromLocalStorage,
  getObjectFromLocalStorage,
  removeDataFromLocalStorage,
  removeCreateCampaignDataFromLocalStorage,
  removeLoginDataFromLocalStorage,
  clearLocalStorage,
  removeClearCacheDataFromLocalStorage,
};
